import {Component, EventEmitter, Input, Output} from '@angular/core';
import { SessionService } from '@edl-development/edl-common';
import { AccessRoleHelper } from 'src/app/model/helpers/access-role.helper';
import { VerificationHistory } from '../..//model/cpl-verification-history.model';
import { ContentsType, CycleDateContents, FollowupDateContents } from '../../model/contents/contents.model';
import { VerificationNote } from '../../model/cpl-verification-note.model';
import { NoteVisibility } from '../../model/enum/note-visibility.enum';
import { EmailStatus } from '../enums/email-status.enum';
import { IndicatorIcon } from '../enums/indicator-icon.enum';
import { IndicatorStyle } from '../enums/indicator-style.enum';

export const NOTE_TO_APPROVERS_LABEL = 'Note to approver(s) - ';
export const NOTE_TO_USER_LABEL = 'Note to user - ';

@Component({
    selector:    'history-item',
    templateUrl: './history-item.component.html',
    styleUrls:   ['./history-item.component.scss']
})
export class HistoryItemComponent {
    @Input() historyItem: VerificationHistory;
    @Input() actionText: string;
    @Input() statusStyle: IndicatorStyle;
    @Input() statusIcon: IndicatorIcon;

    @Output() onMessagesClicked = new EventEmitter<string>();

    readonly EmailStatus = EmailStatus;

    constructor(private sessionService: SessionService)
    {

    }

    get emailStatus(): EmailStatus {
        if (this.historyItem?.MessageIDs?.length > 0) {
            return EmailStatus.SENT;
        }
        if (this.historyItem?.EmailSuppressed) {
            return EmailStatus.SUPPRESSED;
        }
        return EmailStatus.NONE;
    }

    canViewMessages() {
        return AccessRoleHelper.isEDLAdmin(this.sessionService.user.AccessRoleID);
    }

    // Shared date-info block for both compliance-item (CycleDate) and request
    // follow-up (FollowupDate) history entries — reused by every wrapper
    // (UserCycleHistoryItemComponent, UserRequestHistoryItemComponent) since they
    // all render through this component.
    get cycleDateContents(): CycleDateContents | null {
        return this.historyItem?.Contents?.Type === ContentsType.CycleDate ? this.historyItem.Contents : null;
    }

    get followupDateContents(): FollowupDateContents | null {
        return this.historyItem?.Contents?.Type === ContentsType.FollowupDate ? this.historyItem.Contents : null;
    }

    // A date is "unchanged" only when both a previous and new value exist and are
    // equal. On initiation entries previous is absent, so this is always false there
    // — the same template markup renders "assigned" and "unchanged-vs-changed" correctly.
    isDateUnchanged(previous: Date, updated: Date): boolean {
        return !!previous && !!updated && new Date(previous).getTime() === new Date(updated).getTime();
    }

    // A date is "removed" when it previously had a value and now has none — distinct
    // from a field never part of this entry at all (both absent), which renders no row.
    isDateRemoved(previous: Date, updated: Date): boolean {
        return !!previous && !updated;
    }

    getFromNoteText(note: VerificationNote): string {
        if (note.Visibility === NoteVisibility.Supervisor) {
            return NOTE_TO_APPROVERS_LABEL;
        }

        if (note.Visibility === NoteVisibility.EndUser) {
            return NOTE_TO_USER_LABEL;
        }

        // An unrecognised value previously fell through to undefined, which renders the literal
        // string "undefined" in the timeline.
        return '';
    }

    messagesClicked() {
        this.onMessagesClicked.emit(this.historyItem.HistoryID);
    }

    getFromMessageText(message:any){
        return this.decodeHtmlEntities(message);
    }

    private decodeHtmlEntities(text: string): string {
        if (!text) return text;
        const div = document.createElement('div');
        div.innerHTML = text.replace(/<[^>]*>/g, '');
        return div.textContent || '';
    }
}
